import type {
  HtmlDocType,
  HtmlDocTypeWithoutParent,
  HtmlDocTypeWithParent,
  NodeType,
  TextNodeTypeWithoutParent,
  TextNodeTypeWithParent,
} from '../nodeProcessors/node-processors-types';

export function generateTextNodeInSpan(
  text: string,
  dataHtmldiffStart: number,
  dataHtmldiffEnd: number,
  dataHtmldiffTextOffset: number
) {
  const spanNode = wrapTextNodeInSpanWithoutParent({
    parent: null,
    data: text,
    type: 'text',
    prev: null,
    next: null,
  });

  spanNode.attribs['data-htmldiff-start'] = `${dataHtmldiffStart}`;
  spanNode.attribs['data-htmldiff-end'] = `${dataHtmldiffEnd}`;
  spanNode.attribs['data-htmldiff-offset'] = `${dataHtmldiffTextOffset}`;
  spanNode.attribs['data-split'] = 'true';

  return spanNode;
}

export function swapTextNodeWithSpanNodes(
  originalNode: TextNodeTypeWithParent,
  newNodes: Array<HtmlDocType>
) {
  const parent = originalNode.parent;

  // chain the new nodes in prev/next relationship
  let previousNode: HtmlDocType | null = null;
  newNodes.forEach((node) => {
    node.prev = previousNode;
    // Attaching the original node parent
    node.parent = parent;
    if (previousNode != null) {
      previousNode.next = node;
    }
    previousNode = node;
  });
  // set prev and nex from the node that we want to replace
  if (originalNode.prev != null) {
    newNodes[0].prev = originalNode.prev;
    originalNode.prev.next = newNodes[0];
  }
  if (originalNode.next != null) {
    newNodes[newNodes.length - 1].next = originalNode.next;
    originalNode.next.prev = newNodes[newNodes.length - 1];
  }

  let newChildren: Array<TextNodeTypeWithParent | HtmlDocTypeWithParent> = [];

  //Replace the originalNode
  parent.children.forEach((childNode) => {
    if (childNode == originalNode) {
      // @ts-expect-error  I made sure that the ``newNodes`` have ``parent``
      // property set
      newChildren = newChildren.concat(newNodes);
    } else {
      newChildren.push(childNode);
    }
  });

  parent.children = newChildren;
  return parent;
}

export function attachToNewParent(child: HtmlDocType, parent: HtmlDocType) {
  if (child.prev != null) {
    child.prev.next = child.next;
  }

  if (child.next != null) {
    child.next.prev = child.prev;
  }

  const newChild: HtmlDocTypeWithParent = {
    ...child,
    next: null,
    prev: null,
    parent: parent,
  };

  if (parent.children.length > 0) {
    const lastChild = parent.children[parent.children.length - 1];
    lastChild.next = newChild;
    newChild.prev = lastChild;
  }

  parent.children.push(newChild);
}

function replaceNode(
  oldNode: TextNodeTypeWithParent | HtmlDocTypeWithParent,
  newNode: HtmlDocTypeWithoutParent
) {
  const parent = oldNode.parent;

  const toReturnNode: HtmlDocTypeWithParent = {
    ...newNode,
    parent: oldNode.parent,
  };

  const newChildren: Array<TextNodeTypeWithParent | HtmlDocTypeWithParent> = [];
  parent.children.forEach((childNode) => {
    if (childNode === oldNode) {
      newChildren.push(toReturnNode);
    } else {
      newChildren.push(childNode);
    }
  });
  parent.children = newChildren;

  replaceNeighbours(oldNode, toReturnNode);

  return toReturnNode;
}

function generateEmptySpanNode(): HtmlDocTypeWithoutParent {
  return {
    parent: null,
    name: 'span',
    type: 'tag',
    attribs: {},
    children: [],
    prev: null,
    next: null,
  };
}

export function wrapHtmlNodeInSpanWithoutParent(
  htmlNode: HtmlDocTypeWithoutParent
) {
  let spanNode = generateEmptySpanNode();

  const htmlNodeWithoutParent: HtmlDocTypeWithParent = {
    ...htmlNode,
    prev: null,
    next: null,
    parent: spanNode,
  };
  spanNode.children = [htmlNodeWithoutParent];
  replaceNeighbours(htmlNode, spanNode);
  spanNode.attribs.style = 'padding-right: 2px; padding-left: 2px;';
  spanNode.attribs['data-node-virtual'] = 'virtual';
  return spanNode;
}

export function wrapHtmlNodeInSpanWithParent(htmlNode: HtmlDocTypeWithParent) {
  const spanNode = wrapHtmlNodeInSpanWithoutParent({
    ...htmlNode,
    prev: null,
    next: null,
    parent: null,
  });

  return replaceNode(htmlNode, spanNode);
}

export function wrapTextNodeInSpanWithoutParent(
  textNode: TextNodeTypeWithoutParent
): HtmlDocTypeWithoutParent {
  let spanNode = generateEmptySpanNode();

  const textNodeWithParent: TextNodeTypeWithParent = {
    ...textNode,
    prev: null,
    next: null,
    parent: spanNode,
  };
  spanNode.children = [textNodeWithParent];
  replaceNeighbours(textNode, spanNode);
  spanNode.attribs['data-node-virtual'] = 'virtual';
  return spanNode;
}
export function wrapTextNodeInSpanWithParent(textNode: TextNodeTypeWithParent) {
  const spanNode = wrapTextNodeInSpanWithoutParent({
    ...textNode,
    prev: null,
    next: null,
    parent: null,
  });

  return replaceNode(textNode, spanNode);
}

function replaceNeighbours(oldNode: NodeType, newNode: NodeType) {
  if (oldNode.prev != null) {
    oldNode.prev.next = newNode;
  }
  if (oldNode.next != null) {
    oldNode.next.prev = newNode;
  }
  newNode.prev = oldNode.prev;
  newNode.next = oldNode.next;
}
